import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MessageSquare, CheckCircle, Bug, Sparkles } from 'lucide-react';
import { createFeedback } from '../api/client';
import { useIsMobile } from '../hooks/useIsMobile';

export default function FeedbackButton() {
  const isMobile = useIsMobile();
  const backdropMouseDown = useRef(false);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'bug' | 'feature'>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createFeedback(
        { type, title, description: description || undefined, page_url: window.location.pathname },
        screenshot || undefined,
      ),
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
        setTitle('');
        setDescription('');
        setScreenshot(null);
        setType('bug');
        if (fileRef.current) fileRef.current.value = '';
      }, 1500);
    },
  });

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        style={{
          position: 'fixed',
          bottom: isMobile
            ? 'calc(var(--mobile-tab-bar-height, 56px) + var(--safe-bottom, env(safe-area-inset-bottom, 0px)) + var(--mobile-action-gap, 16px))'
            : 24,
          right: isMobile ? undefined : 24,
          left: isMobile ? 16 : undefined,
          width: isMobile ? 40 : 48,
          height: isMobile ? 40 : 48,
          borderRadius: '50%',
          background: 'var(--primary)',
          color: '#fff',
          border: 'none',
          fontSize: '1.3rem',
          cursor: 'pointer',
          zIndex: 1000,
          boxShadow: '0 4px 14px rgba(244, 63, 94, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(244, 63, 94, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 14px rgba(244, 63, 94, 0.4)';
        }}
        title="Send feedback"
      >
        <MessageSquare size={22} />
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) setOpen(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: 'var(--card)',
              borderRadius: 'var(--radius-lg, 12px)',
              width: 'min(440px, 92vw)',
              padding: '1.5rem',
              boxShadow: 'var(--shadow-lg)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)' }}>Send Feedback</h3>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.2rem' }}
              >
                &times;
              </button>
            </div>

            {success ? (
              <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--text)' }}>
                <div style={{ marginBottom: '0.5rem', color: 'var(--success)' }}><CheckCircle size={36} /></div>
                <div style={{ fontWeight: 600 }}>Thank you! Feedback submitted.</div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (title.trim()) mutation.mutate();
                }}
              >
                {/* Type toggle */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {(['bug', 'feature'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        borderRadius: 'var(--radius-sm, 6px)',
                        border: type === t ? '2px solid var(--primary)' : '1px solid var(--border)',
                        background: type === t ? 'var(--primary-light, rgba(244, 63, 94, 0.08))' : 'var(--bg)',
                        color: type === t ? 'var(--primary)' : 'var(--text-muted)',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {t === 'bug' ? <><Bug size={15} /> Bug Report</> : <><Sparkles size={15} /> Feature Request</>}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Title */}
                <input
                  type="text"
                  placeholder="Title (required)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.7rem',
                    borderRadius: 'var(--radius-sm, 6px)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '0.85rem',
                    marginBottom: '0.5rem',
                    boxSizing: 'border-box',
                  }}
                />

                {/* Description */}
                <textarea
                  placeholder="Description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.7rem',
                    borderRadius: 'var(--radius-sm, 6px)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '0.85rem',
                    marginBottom: '0.5rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />

                {/* Page URL (read-only) */}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                  Page: <code style={{ background: 'var(--bg)', padding: '0.1rem 0.3rem', borderRadius: 3 }}>{window.location.pathname}</code>
                </div>

                {/* Screenshot */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                    Screenshot (optional)
                  </label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
                    style={{ fontSize: '0.8rem' }}
                  />
                </div>

                {mutation.isError && (
                  <div style={{ color: '#e53935', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    Failed to submit. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!title.trim() || mutation.isPending}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: 'var(--primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 'var(--radius-sm, 6px)',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: title.trim() && !mutation.isPending ? 'pointer' : 'not-allowed',
                    opacity: title.trim() && !mutation.isPending ? 1 : 0.6,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  {mutation.isPending ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
